import React from 'react';
import clsx from 'clsx';
import {
    Files,
    FileText,
    Clock,
    CheckCircle,
    Plus,
    Copy,
    Trash2,
    Search,
    X,
    Filter,
    Lightbulb,
    Sparkles,
    Save,
    Settings2,
    WrapText,
    AlignLeft,
} from 'lucide-react';
import { PfmeaItem } from '../../types';

interface PfmeaToolbarProps {
    isDark: boolean;
    viewMode: 'all' | 'draft' | 'review' | 'approved';
    setViewMode: (mode: 'all' | 'draft' | 'review' | 'approved') => void;
    statusCounts: { all: number; draft: number; review: number; approved: number };
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    showFilters: boolean;
    setShowFilters: (show: boolean) => void;
    hasActiveFilters: boolean;
    showCoachingTips: boolean;
    setShowCoachingTips: (show: boolean) => void;
    showSmartSuggest: boolean;
    onSmartSuggestClick: () => void;
    handleAddRow: () => void;
    handleDuplicateRow: () => void;
    handleDeleteSelected: () => void;
    handleSave: () => void;
    saving: boolean;
    selectedIds: Set<string>;
    pendingChanges: Record<string, Partial<PfmeaItem>>;
    hasPermission: (p: string) => boolean;
    wrapText: boolean;
    setWrapText: (v: boolean) => void;
    onOpenLayoutSettings: () => void;
}

const PfmeaToolbar: React.FC<PfmeaToolbarProps> = ({
    isDark,
    viewMode,
    setViewMode,
    statusCounts,
    searchTerm,
    setSearchTerm,
    showFilters,
    setShowFilters,
    hasActiveFilters,
    showCoachingTips,
    setShowCoachingTips,
    showSmartSuggest,
    onSmartSuggestClick,
    handleAddRow,
    handleDuplicateRow,
    handleDeleteSelected,
    handleSave,
    saving,
    selectedIds,
    pendingChanges,
    hasPermission,
    wrapText,
    setWrapText,
    onOpenLayoutSettings,
}) => {
    return (
        <div className={clsx(
            'p-3 rounded-xl border flex flex-wrap items-center justify-between gap-3',
            isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
        )}>
            {/* Left: View Mode Toggle */}
            <div className="flex items-center gap-2">
                <div className={clsx('flex rounded-lg p-1', isDark ? 'bg-slate-700' : 'bg-slate-100')}>
                    {[
                        { key: 'all', label: 'All', icon: Files, count: statusCounts.all },
                        { key: 'draft', label: 'Draft', icon: FileText, count: statusCounts.draft },
                        { key: 'review', label: 'Review', icon: Clock, count: statusCounts.review },
                        { key: 'approved', label: 'Approved', icon: CheckCircle, count: statusCounts.approved },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setViewMode(tab.key as any)}
                            className={clsx(
                                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                                viewMode === tab.key
                                    ? 'bg-primary-500 text-white shadow'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            )}
                        >
                            <tab.icon size={14} />
                            {tab.label}
                            <span className={clsx(
                                'ml-1 px-1.5 py-0.5 rounded-full text-xs',
                                viewMode === tab.key ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-600'
                            )}>
                                {tab.count}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Add / Duplicate / Delete */}
                {hasPermission('pfmea.create') && (
                    <>
                        <button
                            onClick={handleAddRow}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                        >
                            <Plus size={16} />
                            Add Row
                        </button>
                        <button
                            onClick={handleDuplicateRow}
                            disabled={selectedIds.size === 0}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50"
                        >
                            <Copy size={16} />
                            Duplicate
                        </button>
                    </>
                )}
                {hasPermission('pfmea.delete') && (
                    <button
                        onClick={handleDeleteSelected}
                        disabled={selectedIds.size === 0}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200 disabled:opacity-50"
                    >
                        <Trash2 size={16} />
                        Delete ({selectedIds.size})
                    </button>
                )}
            </div>

            {/* Right: Search, Filters, Save */}
            <div className="flex items-center gap-2">
                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={clsx(
                            'pl-9 pr-3 py-1.5 text-sm rounded-lg border w-48',
                            isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200'
                        )}
                    />
                    {searchTerm && (
                        <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                            <X size={14} />
                        </button>
                    )}
                </div>

                {/* Filter Toggle */}
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={clsx(
                        'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border',
                        showFilters || hasActiveFilters
                            ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900 dark:border-primary-700'
                            : isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'
                    )}
                >
                    <Filter size={16} />
                    Filters
                </button>

                {/* Coaching Toggle */}
                <button
                    onClick={() => setShowCoachingTips(!showCoachingTips)}
                    className={clsx(
                        'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border',
                        showCoachingTips
                            ? 'bg-amber-100 border-amber-300 text-amber-700 dark:bg-amber-900 dark:border-amber-700'
                            : isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'
                    )}
                    title="Toggle Coaching Tips"
                >
                    <Lightbulb size={16} />
                    Tips
                </button>

                {/* SmartSuggest AI Button */}
                <button
                    onClick={onSmartSuggestClick}
                    className={clsx(
                        'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-all',
                        showSmartSuggest
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-purple-600'
                            : isDark ? 'bg-slate-700 border-slate-600 hover:border-purple-500' : 'bg-white border-slate-200 hover:border-purple-500'
                    )}
                >
                    <Sparkles size={16} />
                    AI Suggest
                </button>

                {/* Wrap Text Quick Toggle */}
                <button
                    onClick={() => setWrapText(!wrapText)}
                    className={clsx(
                        'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-all',
                        wrapText
                            ? 'bg-blue-600 text-white border-blue-700 shadow-sm'
                            : isDark ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    )}
                    title={wrapText ? 'Wrap ON – click to truncate' : 'Wrap OFF – click to wrap'}
                >
                    {wrapText ? <><WrapText size={15} /> Wrap</> : <><AlignLeft size={15} /> No Wrap</>}
                </button>

                {/* Layout Settings */}
                <button
                    onClick={onOpenLayoutSettings}
                    className={clsx(
                        'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-all',
                        isDark ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-primary-600'
                    )}
                    title="Layout & Display Settings"
                >
                    <Settings2 size={15} />
                    Layout
                </button>

                {/* Save */}
                <button
                    onClick={handleSave}
                    disabled={Object.keys(pendingChanges).length === 0 || saving}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                >
                    <Save size={16} />
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </div>
    );
};

export default PfmeaToolbar;
